import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  Chip,
  useTheme,
  InputAdornment,
  Tooltip,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search,
  Edit,
  Delete,
  Add,
  Folder as FolderIcon,
  AccountTree,
  ViewList,
} from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { ConfirmDialog } from '@/components/common';
import { FolderDialog } from '@/components/folders/FolderDialog';
import { useAppDispatch, useAppSelector } from '@/store/types';
import {
  fetchFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  setCurrentFolder,
  clearFolderError,
} from '@/store';
import { Folder, FolderFormData } from '@/constants/types/folder.types';
import { folderService } from '@/services/admin/folderService';
import toast from 'react-hot-toast';

/**
 * Flatten nested folder structure into a flat array while preserving hierarchy
 */
const flattenFolders = (folders: Folder[]): Folder[] => {
  const result: Folder[] = [];

  const flatten = (folderList: Folder[]) => {
    folderList.forEach((folder) => {
      // Add the folder without children to avoid circular references
      const { children, ...folderWithoutChildren } = folder;
      result.push(folderWithoutChildren as Folder);

      // Recursively flatten children
      if (children && children.length > 0) {
        flatten(children);
      }
    });
  };

  flatten(folders);
  return result;
};

export const FoldersPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { folders, pagination, isLoading, isActionLoading, error, } =
    useAppSelector((state) => state.folders);

  const [searchFilter, setSearchFilter] = useState('');
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [groupByParent, setGroupByParent] = useState<boolean>(true);

  useEffect(() => {
    loadFolders();
  }, [searchFilter, paginationState, groupByParent]);

  useEffect(() => {
    loadAllFolders();
  }, []);

  const loadFolders = () => {
    dispatch(
      fetchFolders({
        page: paginationState.pageIndex + 1,
        limit: paginationState.pageSize,
        sort: 'level,name',
        groupByParent: groupByParent,
        ...(searchFilter && { search: searchFilter }),
      })
    );
  };

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: boolean | null
  ) => {
    if (newView !== null) {
      setGroupByParent(newView);
    }
  };

  const loadAllFolders = async () => {
    try {
      const response = await folderService.getFolders({
        limit: 1000,
        sort: 'level,name',
        groupByParent: false, // Don't group for dropdown
      });
      if (response.success) {
        // Flatten if nested
        const flatFolders = response.data.folders[0]?.children
          ? flattenFolders(response.data.folders)
          : response.data.folders;
        setAllFolders(flatFolders);
      }
    } catch (err) {
      console.error('Failed to load all folders for dropdown:', err);
    }
  };

  const handleCreateClick = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async (data: FolderFormData) => {
    try {
      const result = await dispatch(createFolder(data)).unwrap();
      toast.success(result.message || 'Folder created successfully');
      setCreateDialogOpen(false);
      loadFolders();
      loadAllFolders();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create folder');
    }
  };

  const handleEditClick = (folder: Folder) => {
    dispatch(setCurrentFolder(folder));
    setSelectedFolder(folder);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (data: FolderFormData) => {
    if (!selectedFolder) return;

    try {
      const result = await dispatch(
        updateFolder({ id: selectedFolder._id, data })
      ).unwrap();
      toast.success(result.message || 'Folder updated successfully');
      setEditDialogOpen(false);
      setSelectedFolder(null);
      dispatch(setCurrentFolder(null));
      loadFolders();
      loadAllFolders();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update folder');
    }
  };

  const handleDeleteClick = (folder: Folder) => {
    setSelectedFolder(folder);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFolder) return;

    try {
      await dispatch(deleteFolder(selectedFolder._id)).unwrap();
      toast.success('Folder deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedFolder(null);
      loadFolders();
      loadAllFolders();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete folder');
    }
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedFolder(null);
    dispatch(setCurrentFolder(null));
  };

  const handleErrorDismiss = () => {
    dispatch(clearFolderError());
  };

  // Flatten the nested folder structure for table display
  const flatFolders = useMemo(() => {
    if (!folders || folders.length === 0) return [];

    // Check if the response is already flat or nested
    const hasChildren = folders.some(f => f.children && f.children.length > 0);

    if (hasChildren) {
      return flattenFolders(folders);
    }

    return folders;
  }, [folders]);

  const columns = useMemo<MRT_ColumnDef<Folder>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Folder Name',
        size: 250,
        Cell: ({ row }) => {
          const indentLevel = row.original.level - 1;
          const indentPx = indentLevel * 24;

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pl: `${indentPx}px`,
              }}
            >
              <FolderIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              <Typography variant="body2" fontWeight="medium">
                {row.original.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'level',
        header: 'Level',
        size: 80,
        Cell: ({ cell }) => (
          <Chip
            label={`L${cell.getValue<number>()}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {new Date(cell.getValue<string>()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last Modified',
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {new Date(cell.getValue<string>()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit Folder">
              <IconButton
                size="small"
                onClick={() => handleEditClick(row.original)}
                sx={{ color: 'primary.main' }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Folder">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(row.original)}
                sx={{ color: 'error.main' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: flatFolders,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: false,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: pagination?.total || 0,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Folders Management
          </Typography>
          <Chip
            label={`${pagination?.total || 0} Folders`}
            color="primary"
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={groupByParent}
            exclusive
            onChange={handleViewChange}
            aria-label="folder view mode"
            size="small"
          >
            <ToggleButton value={true} aria-label="nested view">
              <Tooltip title="Nested View">
                <AccountTree fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={false} aria-label="flat view">
              <Tooltip title="Flat View">
                <ViewList fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ textTransform: 'none' }}
          >
            Create Folder
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleErrorDismiss}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search folders..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>
        </Grid>
      </Box>

      <MaterialReactTable table={table} />

      <FolderDialog
        open={createDialogOpen || editDialogOpen}
        folder={editDialogOpen ? selectedFolder : null}
        availableFolders={allFolders}
        onClose={handleDialogClose}
        onSubmit={editDialogOpen ? handleEditSubmit : handleCreateSubmit}
        isLoading={isActionLoading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Folder"
        message={`Are you sure you want to delete the folder "${selectedFolder?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDialogClose}
        severity="error"
      />
    </Box>
  );
};

export default FoldersPage;
